import { useEffect, useRef } from 'react';
import { useGlobalStore } from '../../stores/global-store';
import { useFiltersStore } from '../../stores/filters-store';
import { useUserStore } from '../../stores/user-store';
import { useDeviceStore } from '../../stores/device-store';
import { useModalStore } from '../../stores/modal-store';
import { useToastStore } from '../../stores/toast-store';
import {
  getFiltersJson,
  getSignupSvg,
  isEmailExcluded,
  login,
  trimUrlForPopups,
  useHttpReqStore
} from '../../api/http-req';
import { fetchUserDetails, fetchFilters, fetchProjectIds, fetchCuratedPlaylists } from '../../queries/bootstrap';
import * as spotifyUserAccount from '../../api/spotify/spotify-user-account';
import * as youtubeUserAccount from '../../api/youtube/youtube-user-account';
import { initGoogleAccount } from '../../api/youtube/youtube-base';
import { whiteLabelUser } from '../../api/white-label-user';
import { navigate } from '../../app/navigation';
import { environment } from '../../config/environment';
import { ModalType } from '../../interfaces/modal-types.enum';
import type { Filter } from '../../interfaces/filter';
import type { User } from '../../interfaces/user';
import { PIXELATE_IN_OUT_ENTER_CLASS } from '../../animations/pixelateInOut';
import ResultSkeleton from '../../shared/components/ResultSkeleton';
import Player from '../../shared/components/Player';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import ResultsGrid from '../results/ResultsGrid';
import { useView } from '../view/use-view';
import './MainContent.scss';

// Port of src/app/components/main-content/ (mypart-main-content).
//
// Lifecycle mapping:
// - constructor side effects (getSignupSvg + playlist-size effect)  → mount
//   effects below.
// - ngOnInit (white-label auto-login, /explorer-signup, /signup,
//   /affiliate-program handling, startLoadingProcess incl. queryParams)
//   → the init effect (guarded against StrictMode double-run).
// - ngAfterViewInit (youtubeBaseApiService.initGoogleAccount) → mount effect.
// - ActivatedRoute.queryParams → read from window.location.search; the router
//   wrapper (app/router.tsx MainContentRoute) passes location.search as the
//   `routeKey` prop, so handleQueryParams re-runs whenever the query string
//   changes on a client-side navigation (Angular's queryParams emissions).
//
// Template notes:
// - The songs @for grid (result items + "Similar Songs" empty-line separators
//   + infinite scroll) is the <ResultsGrid /> contract.
//   TODO(port-integration): ResultsGrid is expected to render its items as
//   children of the #scrollTarget CSS grid (Angular rendered the result items
//   directly inside that grid container).
// - The Angular 'slideInOut' :leave tween is not ported (elements unmount
//   immediately); :enter is the .slide-in-out-enter CSS class.

const skeletonArray = Array.from({ length: 30 }, (_, i) => i);

const mobilePlaylistImgAlt = 'The image shows a red icon with three horizontal bars stacked vertically on the left ' +
  'side and a downward-pointing arrow on the right side. Each element is outlined in white, making ' +
  'them stand out against the black background. The three bars represent a list, and the downward ' +
  'arrow indicates an action related to viewing or expanding this list, such as opening or viewing ' +
  'a playlist. The design is clean and modern, clearly communicating its function.';

export interface MainContentProps {
  /** Changes whenever the route/query string changes — re-runs handleQueryParams. */
  routeKey?: string;
}

function handleYoutube(code: string, user: User) {
  youtubeUserAccount.handleAuthenticationCallback(code)
    .then(res => {
      const userEmail = useUserStore.getState().user!.email;
      return youtubeUserAccount.saveUserData(res.access_token, res.refresh_token, userEmail)
        .then(userProfile => {
          user.isYoutubeConnected = true;
          useUserStore.getState().setUser(user);
          return userProfile;
        });
    })
    .then((value: any) => {
      useToastStore.getState().success(`Successfully connected ${value.names[0].displayName} to your account`);
    })
    .catch(() => {
      // NOTE(port): the Angular source said "Spotify" in the YouTube handler — kept verbatim.
      useToastStore.getState().warning(`Could not connect to your Spotify account. ${environment.toastSupportMessage}`);
    });
}

function handleSpotify(code: string, user: User) {
  spotifyUserAccount.handleAuthenticationCallback(code)
    .then(res => {
      const userEmail = useUserStore.getState().user!.email;

      return spotifyUserAccount.saveUserData(res.access_token, res.refresh_token, userEmail)
        .then(userProfile => {
          user.isSpotifyConnected = true;
          spotifyUserAccount.setUserId(userProfile.id);
          useUserStore.getState().setUser(user);
          return userProfile;
        });
    })
    .then(userProfile => {
      useToastStore.getState().success(`Successfully connected ${userProfile.display_name} to your account`);
    })
    .catch(() => {
      useToastStore.getState().error(`Could not connect to your Spotify account. ${environment.toastSupportMessage}`);
    });
}

function handleQueryParams(params: Record<string, string>, user: User) {
  if (Object.keys(params).length === 0) {
    return;
  }
  const globalStore = useGlobalStore.getState();
  const filtersStore = useFiltersStore.getState();

  // -1 is for quick tags with filters
  // -2 is for quick tags with no filters
  let referenceSongId: number | string | undefined = params['referenceSongIds'] || -1;
  const filterArray: { id: number, state: 'green' | 'red' }[] = [];

  // Iterate over all query parameters to handle filter_ids
  for (const key in params) {
    if (key.startsWith('id_')) {
      const id = +key.split('_')[1];  // Extracts the ID from the key, i.e., 496 from id_496
      const state = params[key] as 'green' | 'red'; // The associated state, e.g., 'green' or 'red'

      filterArray.push({ id, state });
    }
  }

  const primaryFilterId: number | undefined = params['primaryFilterId'] ? +params['primaryFilterId'] : undefined;
  const lyricsContains: string | undefined = params['lyricsContains'];
  const searchInArtist: string | undefined = params['searchInArtist'];
  const multipleArtworksIds: string | undefined = params['multipleArtworksIds'];
  const searchOnThisArtist: string | undefined = params['searchOnThisArtist'];
  const narrative: string | undefined = params['narrative'];

  if (filterArray.length > 0 || primaryFilterId || lyricsContains || searchInArtist || multipleArtworksIds || searchOnThisArtist || narrative) {
    filtersStore.applyFilterFromUrl(filterArray, primaryFilterId, lyricsContains, searchInArtist, multipleArtworksIds, searchOnThisArtist, narrative);
  }

  // NOTE(port): Angular's `params['sortByIndex']` was a string — '0' is truthy
  // there, so the string is checked before converting to a number.
  const sortByIndex: string | undefined = params['sortByIndex'];
  if (sortByIndex) {
    const sortByArray = filtersStore.sortByArray;
    filtersStore.changeSelectedSort(sortByArray[+sortByIndex]);
  }

  if (referenceSongId === -1 && filterArray.length === 0 && !primaryFilterId && !lyricsContains) {
    referenceSongId = -2;
  }

  const youtubeId: string | undefined = params['youtubeId'];

  if (youtubeId) {
    useModalStore.getState().open(ModalType.YOUTUBE_URL_WINDOW, {
      inputs: {
        youtubeId: youtubeId
      }
    });
  }

  filtersStore.setYoutubeIdFromUrl(youtubeId);

  const currentUrl = window.location.href;

  if (currentUrl.includes('/artist/') || currentUrl.includes('/similar/')) {
    return;
  }

  // NOTE(port): from the URL referenceSongId is a string (Angular's Params are
  // strings too) — consumers compare loosely, so the value passes through as-is.
  globalStore.setGlobalField('referenceSongFromUrl', referenceSongId as number);
  // Angular: `globalStateService.updateGlobalStateSignal = ''` — the setter
  // ignored its argument and set a fresh random string.
  globalStore.updateGlobalState();

  const stripeParam = params['stripe'];
  if (stripeParam) {
    useModalStore.getState().open(ModalType.PURCHASE_SUCCESS, {
      inputs: {
        isSuccess: stripeParam === 'suc' // or false for failure
      }
    });
    navigate('/');
  }
  const code = params['code'];
  if (code) {
    if (params['youtube_auth']) {
      handleYoutube(code, user);
    } else {
      handleSpotify(code, user);
    }
  }
}

async function updateFiltersWithJsonDataAndGetIds(): Promise<Filter[]> {
  // Fetch filters from both methods
  const dbFilters = await fetchFilters();
  const jsonFilters = await getFiltersJson();
  useGlobalStore.getState().setGlobalField('publicProjectIds', await fetchProjectIds() as number[]);
  useUserStore.getState().setProjectIds();

  // Create a map to store filters from getFilters() by their id
  const dbFilterMap = new Map<number, any>();
  for (const dbFilterGroup of dbFilters) {
    for (const filter of dbFilterGroup.filters) {
      dbFilterMap.set(filter.id, filter);
    }
  }

  // Iterate through jsonFilters and update them based on dbFilterMap
  for (const jsonFilterGroup of jsonFilters) {
    for (const jsonFilter of jsonFilterGroup.filters) {
      const matchingDbFilter = dbFilterMap.get(jsonFilter.id);
      if (matchingDbFilter) {
        // Update threshold and limit properties in jsonFilter
        jsonFilter.threshold = matchingDbFilter.threshold;
        jsonFilter.limit = matchingDbFilter.limit;
      }
    }
  }

  // Return the updated jsonFilters
  return jsonFilters;
}

function getCuratedPlaylist(): Promise<void> {
  return fetchCuratedPlaylists()
    .then((curatedPlaylists) => {
      useGlobalStore.getState().setGlobalField('curatedPlaylists', curatedPlaylists);
      useGlobalStore.getState().setGlobalField('errorRetrievingPlaylists', false);
    })
    .catch(() => {
      useGlobalStore.getState().setGlobalField('errorRetrievingPlaylists', true);
    });
}

function getQueryParamsFromLocation(): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(window.location.search).entries());
}

// Angular's bootstrap subscribed to route.queryParams with the user CAPTURED in
// the closure (`this.handleQueryParams(params, user)`), and for default users
// setUser is never called (isEmailExcluded is false for emailsToLogout entries),
// so the store user stays null. Later navigations must therefore use the
// captured bootstrap user, not the store user.
let queryParamsUser: User | null = null;

function startLoadingProcess(loginWhiteLabelUser: boolean): void {
  const promises: Promise<void>[] = [];

  promises.push(getCuratedPlaylist());

  if (loginWhiteLabelUser) {
    const filtersPromise = updateFiltersWithJsonDataAndGetIds()
      .then((filters) => {
        useFiltersStore.getState().setFilters(filters);
        // Angular subscribed to route.queryParams here; the React port reads
        // location.search now and re-runs via the routeKey effect on changes.
        handleQueryParams(getQueryParamsFromLocation(), useUserStore.getState().user!);
      });

    promises.push(filtersPromise);
  } else {
    const userDetailsPromise = fetchUserDetails()
      .then(async (user) => {
        if (user) {
          if (isEmailExcluded(user.email)) {
            useUserStore.getState().setUser(user);
          } else {
            useUserStore.getState().initPreferences(null);
          }
          const filters = await updateFiltersWithJsonDataAndGetIds();
          useFiltersStore.getState().setFilters(filters);
          // Router-wired via the routeKey effect (see above). Capture the
          // bootstrap user for later route changes (Angular closure capture).
          queryParamsUser = user;
          handleQueryParams(getQueryParamsFromLocation(), user);
        }
      });

    promises.push(userDetailsPromise);
  }

  // Wait for all promises to complete
  Promise.all(promises).then(() => {
    useGlobalStore.getState().setGlobalField('initialLoadingProcessComplete', true); // Set to true after everything is done
    console.log('Loading process completed');
  });
}

export default function MainContent({ routeKey }: MainContentProps) {
  const view = useView();
  const songs = useGlobalStore(s => s.songs);
  const showNoMoreResults = useGlobalStore(s => s.showNoMoreResults);
  const showPlaylist = useGlobalStore(s => s.showPlaylist);
  const noMoreResultsMessage = useGlobalStore(s => s.noMoreResultsMessage);
  const playlistSongs = useGlobalStore(s => s.playlistSongs);
  const isMaxTabletDevice = useDeviceStore(s => s.isMaxTabletDevice);
  const filtersVisible = useFiltersStore(s => s.filtersVisible);
  const prompterText = useFiltersStore(s => s.prompterText);
  const songFromUrl = useHttpReqStore(s => s.songFromUrl);
  const artistFromUrl = useHttpReqStore(s => s.artistFromUrl);

  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const currentPlaylistSizeRef = useRef(0);
  const didInitRef = useRef(false);

  const scrollToMainContent = () => {
    setTimeout(() => {
      if (scrollTargetRef.current) {
        // Get the element's position relative to the entire document
        const elementPosition = scrollTargetRef.current.offsetTop;

        setTimeout(() => {
          window.scrollTo({
            top: elementPosition - (useGlobalStore.getState().navBar?.offsetHeight ?? 0),
            behavior: 'smooth'
          });
        }, 300);
      }
    });
  };

  // constructor + ngOnInit + ngAfterViewInit (run once, StrictMode-guarded).
  useEffect(() => {
    if (didInitRef.current) {
      // Route changed after init: re-run only the query-param handling
      // (Angular's route.queryParams subscription fired on navigation, with
      // the bootstrap user captured in its closure — store user is null for
      // default users, so prefer the captured one).
      const user = useUserStore.getState().user ?? queryParamsUser;
      if (user) {
        try {
          handleQueryParams(getQueryParamsFromLocation(), user);
        } catch (err) {
          // Angular's queryParams subscription errors were logged by Zone and
          // the app survived; a throw here would unmount MainContent via the
          // router's ErrorBoundary instead.
          console.error('handleQueryParams failed:', err);
        }
      }
      return;
    }
    didInitRef.current = true;

    getSignupSvg();

    const globalStore = useGlobalStore.getState();
    const modalStore = useModalStore.getState();
    const currentUrl = window.location.href;
    let loginWhiteLabelUser = false;

    if (whiteLabelUser.whiteLabelEmail) {
      // (view derives 'home' from the white-label landing URL's home-keeping params)
      loginWhiteLabelUser = true;
      const email = whiteLabelUser.whiteLabelEmail;
      const pass = whiteLabelUser.whiteLabelPass;
      if (email && pass) {
        setTimeout(() => {
          login(email, pass)
            .then((user: User) => {
              useUserStore.getState().setUser(user);
            })
            .catch(() => {
              useToastStore.getState().warning('We could not sign you in automatically, please sign in again.');
            })
            .finally(() => {
              whiteLabelUser.whiteLabelEmail = undefined;
              whiteLabelUser.whiteLabelPass = undefined;
            });
        });
      }
    }

    if (currentUrl.includes('/explorer-signup')) {
      modalStore.open(ModalType.LOGIN_REGISTER, {
        inputs: {
          loginPopupTitle: 'Sign Up for FREE',
          onlyExplorer: true
        }
      });
    }

    if (currentUrl.includes('/signup')) {
      // (view derives 'home' for the /signup landing — modal renders over home)
      const queryParams = new URLSearchParams(window.location.search);
      const promotionCode = queryParams.get('promocode') || queryParams.get('amp;promocode');
      const affiliateCode = queryParams.get('affiliate');

      // Check for Overwolf UTM source
      const userStore = useUserStore.getState();
      userStore.checkUtmTags(true);
      const isOverwolfMode = (() => {
        const utmTags = useUserStore.getState().utmTags;
        if (!utmTags) return false;
        return new URLSearchParams(utmTags).get('utm_source')?.toLowerCase() === 'overwolf';
      })();

      // Extract email, name, and password for Overwolf pre-fill
      const prefillEmail = queryParams.get('email') || '';
      const prefillName = queryParams.get('name') || '';
      const prefillPassword = queryParams.get('pass') || '';

      const inputs: any = {
        isOverwolfMode,
        prefillEmail,
        prefillName,
        prefillPassword
      };

      // Hide close button for Overwolf landing
      if (isOverwolfMode) {
        inputs.hideCloseButton = true;
        inputs.loginPopupTitle = 'Welcome Overwolf Gamer!';
      }

      if (promotionCode) {
        inputs.promotionCodeFromUrl = promotionCode;
      }
      if (affiliateCode) {
        inputs.affiliateCode = affiliateCode;
        inputs.loginPopupTitle = 'You have been invited to Songhunt.ai';
      }
      modalStore.open(ModalType.LOGIN_REGISTER, { inputs });
    }

    if (currentUrl.includes('affiliate-program/')) {
      modalStore.open(ModalType.AFFILIATION_LINK);
      // (view derives 'home' for the affiliate landing — modal renders over home)
      window.history.pushState({}, 'Songhunt', trimUrlForPopups(window.location.href, 'affiliate-program/'));
    }

    startLoadingProcess(loginWhiteLabelUser);

    // ngAfterViewInit
    initGoogleAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // Port of the constructor effect() on playlistSongs (scale / underline-fill
  // animation classes on the Edit/Export Playlist bar).
  useEffect(() => {
    const imageElement = document.querySelector('.scale-effect img');
    const spanElement = document.querySelector('.new-underline-effect');

    if (currentPlaylistSizeRef.current > playlistSongs.size) {
      // Playlist size decreased, scale down the icon to 90% and back to normal size
      imageElement?.classList.add('scale-down');
      // Trigger fill-down and backwards effect when playlist size decreases
      spanElement?.classList.add('fill-down');
    }

    if (currentPlaylistSizeRef.current < playlistSongs.size) {
      // Playlist size increased, scale up the icon to 110% and back to normal size
      imageElement?.classList.add('scale-up');

      // Trigger fill-up and backwards effect when playlist size increases
      spanElement?.classList.add('fill-up');
    }

    const timer = setTimeout(() => {
      imageElement?.classList.remove('scale-up');
      imageElement?.classList.remove('scale-down');
      spanElement?.classList.remove('fill-up');
      spanElement?.classList.remove('fill-down');
    }, 1000);
    currentPlaylistSizeRef.current = playlistSongs.size;
    return () => clearTimeout(timer);
  }, [playlistSongs]);

  // NOTE(port): in Angular this was a `computed()` created in ngOnInit that was
  // never read, so it never actually executed; the clear intent (scroll the
  // results into view when a search is described/triggered) is ported as an
  // active effect.
  useEffect(() => {
    if (prompterText || songFromUrl || artistFromUrl) {
      scrollToMainContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompterText, songFromUrl, artistFromUrl]);

  const setShowPlaylistTrue = () => useGlobalStore.getState().setGlobalField('showPlaylist', true);

  return (
    <section className="flex h-full">
      <section className={`w-full ${!isMaxTabletDevice ? 'w-[calc(100%-60px)]' : ''}`}>
        <section
            className={`h-full overflow-y-auto overflow-x-hidden ${showNoMoreResults && view !== 'home' ? 'w-full' : ''}`}
            id="results-scroll-container">
          <div ref={scrollTargetRef}
               className="p-2 mt-2 sm:mt-0 md:p-4 w-full h-full grid gap-4 grid-cols-fit-sm sm:grid-cols-fit-lg"
               id="scrollTarget">
            {/* Show Skeleton */}
            {view !== 'home' && view !== 'curated' && !showNoMoreResults && songs.length === 0 &&
              skeletonArray.map((item) => (
                <div key={item} className={`${PIXELATE_IN_OUT_ENTER_CLASS} max-w-[400px]`}>
                  <ResultSkeleton/>
                </div>
              ))}
            {/* Songs grid + infinite scroll (cross-agent contract). */}
            {view !== 'home' && songs.length > 0 && <ResultsGrid/>}
            {showNoMoreResults && view !== 'home' && (
              <div className="h-full w-[100vw] flex justify-center items-end p-4 z-10">
                <div
                    className="flex items-center p-6 rounded-xl bg-mypart_red-500/10 dark:bg-mypart_red-300/20 backdrop-blur-sm mb-4 shadow-lg max-w-[90vw] border border-mypart_red-300/30 dark:border-mypart_red-300/50"
                    role="alert">
                  <svg aria-hidden="true" className="flex-shrink-0 w-6 h-6 text-mypart_red-500 dark:text-mypart_red-300"
                       fill="currentColor"
                       viewBox="0 0 20 20"
                       xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
                  </svg>
                  <span className="sr-only">Info</span>
                  {/* Angular: [innerHTML]="getSanitizedMessage()" — the message is an
                      app-internal string (see getDefaultNoMoreResultsMessage), not user input. */}
                  <div dangerouslySetInnerHTML={{ __html: noMoreResultsMessage }}
                       className="ml-4 text-base lg:text-lg font-medium text-center lg:text-left text-gray-800 dark:text-gray-100">
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* The player drives Spotify/YouTube iframes + audio; if it crashes
              it shouldn't take the results grid down with it. */}
          <ErrorBoundary name="player" fallback={null}>
            <Player/>
          </ErrorBoundary>
        </section>
      </section>

      {/* Playlist bar */}
      {view !== 'home' && (
        isMaxTabletDevice ? (
          <section className={`z-50 w-[40px] absolute right-0 h-[120px] transition-all ${filtersVisible ? 'top-[300px] ' : 'top-[240px] '}`}>
            <div onClick={setShowPlaylistTrue}
                 onMouseDown={setShowPlaylistTrue}
                 className="mobile-playlist-bar main-theme slide-in-out-enter">
              <figure className="rotate-90 w-6 h-5 relative scale-effect">
                <img alt={mobilePlaylistImgAlt}
                     className="absolute inset-0 w-full h-full"
                     src="https://d382nvfdu38z2f.cloudfront.net/songhunt/mobile_playlist.png"/>
              </figure>
            </div>
          </section>
        ) : (
          <section
              className="w-[56px] mx-[2px] mt-[4px] border border-gray-300 dark:border-gray-700 h-[calc(100%-8px)] z-50 shadow-2xl">
            {!showPlaylist && (
              <div onClick={setShowPlaylistTrue}
                   className="main-theme !bg-transparent flex justify-center items-center cursor-pointer text-gray-700 dark:text-gray-400
            hover:text-mypart_red-500 dark:hover:text-mypart_red-500 slide-in-out-enter">
                <div className="rotate-90 relative flex flex-row items-end">
                  <span className="text-lg new-underline-effect">Edit/Export Playlist</span>
                  <figure className="w-6 h-5 ml-2 relative scale-effect">
                    <img alt={mobilePlaylistImgAlt}
                         className="absolute inset-0 w-full h-full"
                         src="https://d382nvfdu38z2f.cloudfront.net/songhunt/mobile_playlist.png"/>
                  </figure>
                </div>
              </div>
            )}
          </section>
        )
      )}
    </section>
  );
}
